import { useState } from 'react'
import { Megaphone, Save, BarChart3, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, X } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { generateId, detectReportType, validateReportFields, formatFileSize } from '../utils/helpers'

const reportTypes = ['Search Term Report', 'Campaign Report', 'Targeting Report', 'Placement Report', 'Business Report', 'Advertised Product Report', 'Purchased Product Report', 'Budget Report', 'ABA / Brand Analytics', 'ERP导出', 'Unknown Report']

export default function AdsWorkbench() {
  const { agents, prompts, knowledgeBases, tasks, setTasks, canExecute } = useApp()
  const adsAgents = agents.filter(a => a.module === 'Ads' && (a.status === '主用' || a.status === '测试'))
  const workflowAgents = adsAgents.filter(a => a.agentType === 'workflow')
  const singleAgents = adsAgents.filter(a => a.agentType === 'single')

  const [agentId, setAgentId] = useState('')
  const [stepId, setStepId] = useState('')
  const [reports, setReports] = useState([])
  const [output, setOutput] = useState('')
  const [loading, setLoading] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const agent = agents.find(a => a.id === agentId)
  const step = agent?.steps?.find(s => s.stepId === stepId)

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const files = Array.from(e.dataTransfer.files); processFiles(files) }
  const handleFileSelect = (e) => { const files = Array.from(e.target.files); processFiles(files) }

  const processFiles = (files) => {
    const newReports = files.map(file => {
      const type = detectReportType(file.name)
      const headers = ['Campaign','Ad Group','Targeting','Customer Search Term','Match Type','Impressions','Clicks','CTR','CPC','Spend','Sales','Orders','CVR','ACOS','ROAS']
      const validation = validateReportFields(headers)
      return { id: generateId(), fileName: file.name, fileSize: file.size, type, headers: validation.missing.length > 0 ? validation.missing : headers.slice(0,8), validation, parsed: false }
    })
    setReports([...reports, ...newReports])
  }

  const execute = () => {
    if (!canExecute()) return alert('无权限')
    if (!agentId) return alert('请选择Agent')
    if (agent?.agentType === 'workflow' && !stepId) return alert('请选择Step')
    if (reports.length === 0) return alert('请上传广告报表')
    setLoading(true)
    setTimeout(() => {
      setOutput(`【广告诊断结果】\n\n=== 数据完整性评分 ===\n${reports.map(r => `- ${r.fileName}: ${r.validation.score}/100 ${r.validation.missing.length > 0 ? '(缺失: ' + r.validation.missing.join(', ') + ')' : ''}`).join('\n')}\n\n=== 报表类型识别 ===\n${reports.map(r => `- ${r.fileName} -> ${r.type}`).join('\n')}\n\n=== 诊断发现 ===\n1. Campaign_Auto_Broad: ACOS 42% | 花费$350 | 建议: 否定低效词, 降低竞价15%\n2. Campaign_Exact_Low: ACOS 14% | 花费$200 | 建议: 增加预算50%, 提升竞价10%\n\n=== 否定关键词建议 ===\n- free (无关流量)\n- cheap (低转化)\n- used (不匹配)\n\n=== 今日执行清单 ===\n[ ] 执行否定关键词更新\n[ ] 调整 Campaign 预算\n[ ] 监控3天后 ACOS 变化\n\n=== 7 天监控计划 ===\nDay 1: 执行否定词 + 预算调整\nDay 3: 检查 ACOS 变化趋势\nDay 7: 输出周度优化报告`)
      setLoading(false)
    }, 1500)
  }

  const save = () => {
    const newTask = { id: generateId(), name: '广告诊断 ' + new Date().toLocaleDateString('zh-CN'), type: 'Ads',
      workflowId: agent?.agentType === 'workflow' ? agent.id : '', stepId: stepId || '',
      agentId, promptVersion: step ? '-' : (prompts.find(p => p.id === agent?.promptId)?.version || '-'),
      knowledgeVersion: knowledgeBases.filter(k => (step?.knowledgeIds || agent?.knowledgeIds || []).includes(k.id)).map(k => k.version).join(', ') || '-',
      model: step?.defaultModel || agent?.defaultModel || '-', inputFiles: reports.map(r => r.id),
      inputText: `上传了 ${reports.length} 个报表`, aiOutput: output, humanEdit: null,
      adopted: false, reviewStatus: '待审核', owner: 'Leo', reviewer: '',
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    setTasks([newTask, ...tasks]); alert('已保存')
  }

  const removeReport = (id) => setReports(reports.filter(r => r.id !== id))

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Megaphone size={24} className="text-orange-600" />
        <h2 className="text-xl font-bold text-slate-800">广告诊断台</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">选择广告 Agent / 工作流</label>
            <select className="select" value={agentId} onChange={e => { setAgentId(e.target.value); setStepId('') }}>
              <option value="">请选择...</option>
              {workflowAgents.length > 0 && <optgroup label="工作流">{workflowAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>}
              {singleAgents.length > 0 && <optgroup label="单 Agent">{singleAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>}
            </select>
          </div>

          {agent?.agentType === 'workflow' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">选择 Step</label>
              <div className="flex gap-2 flex-wrap">
                {agent.steps?.map(s => (
                  <button key={s.stepId} onClick={() => setStepId(s.stepId)}
                    className={`px-3 py-1.5 rounded-lg text-sm ${stepId === s.stepId ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {s.stepNumber}. {s.stepName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Report Upload */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`p-4 border-2 border-dashed rounded-lg text-center transition-colors ${dragOver ? 'border-orange-500 bg-orange-50' : 'border-slate-300'}`}
          >
            <Upload size={24} className={`mx-auto mb-2 ${dragOver ? 'text-orange-600' : 'text-slate-400'}`} />
            <p className="text-sm text-slate-600 font-medium">拖拽广告报表到此处</p>
            <p className="text-xs text-slate-400 mt-1">支持 Search Term / Campaign / Targeting / Placement / Business / Budget / ABA / ERP导出 / Excel / CSV</p>
            <input type="file" multiple accept=".csv,.xlsx,.xls" className="hidden" id="ad-report-input" onChange={handleFileSelect} />
            <label htmlFor="ad-report-input" className="mt-2 inline-block text-xs text-orange-600 hover:underline cursor-pointer">点击选择文件</label>
          </div>

          {reports.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700">已上传报表 ({reports.length})</div>
              {reports.map(r => (
                <div key={r.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm">
                  <FileSpreadsheet size={14} className="text-emerald-600" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{r.fileName} <span className="text-xs text-slate-400">({formatFileSize(r.fileSize)})</span></div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-500">{r.type}</span>
                      {r.validation.valid ? <span className="text-emerald-600 flex items-center gap-0.5"><CheckCircle2 size={10} /> 字段完整</span>
                        : <span className="text-amber-600 flex items-center gap-0.5"><AlertCircle size={10} /> 缺失 {r.validation.missing.length} 项</span>}
                    </div>
                  </div>
                  <button onClick={() => removeReport(r.id)} className="p-1 hover:bg-red-50 rounded"><X size={12} className="text-red-500" /></button>
                </div>
              ))}
            </div>
          )}

          <button onClick={execute} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
            <BarChart3 size={16} /> {loading ? '诊断中...' : '执行广告诊断'}
          </button>
        </div>

        <div className="space-y-4">
          {agent && (
            <div className="card p-4 bg-orange-50 border-orange-200">
              <div className="text-sm text-orange-800 font-medium mb-1">当前: {agent.name} {step && `- ${step.stepName}`}</div>
              <div className="text-xs text-orange-600">Prompt: {prompts.find(p => p.id === (step?.systemPromptId || agent.promptId))?.name || '无'}</div>
            </div>
          )}

          {output && (
            <div className="card p-5">
              <h3 className="font-semibold text-slate-800 mb-3">诊断结果 & 动作表</h3>
              <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap font-mono min-h-[200px]">{output}</div>
              <div className="flex gap-3 mt-4"><button onClick={save} className="btn-primary flex items-center gap-2"><Save size={14} /> 保存复盘</button></div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
